<template>
  <div class="d-flex flex-column">
    <div
      class="d-flex align-center justify-space-between"
      style="min-height: 36px"
    >
      <div class="d-flex align-center">
        <table-name
          :table-number="tableNumber"
          :table-prefix="tablePrefix"
          :table-suffix="tableSuffix"
          :custom-name="customName"
          class="text-xs mb-1"
        />
        <auto-extension-badge
          :enabled="autoExtensionEnabled"
          class="ml-1"
        />
      </div>

      <div
        v-if="addedCustomerTags.length > 0"
        class="d-flex align-center"
      >
        <v-icon
          left
          small
        >
          {{ icons.mdiPound }}
        </v-icon>

        <div style="max-width: 240px">
          <v-chip-group class="customer-tags-cap-slide">
            <v-chip
              v-for="(customerTag) in addedCustomerTags"
              :key="`${tablePrefix}${tableNumber}${tableSuffix}-customerTag${customerTag.id}`"
              small
            >
              {{ customerTag.attributes.name }}
            </v-chip>
          </v-chip-group>
        </div>
      </div>
    </div>

    <v-card
      class="elevation-4 position-relative"
      min-height="236.5"
      :ripple="false"
      :style="borderStyle"
      @click="$emit('clicked')"
    >
      <table-timer
        :start-at="startAt"
        :expire-at="expireAt"
        :finish-notice-minutes-date="finishNoticeMinutesDate"
        :finish-notice-color="finishNoticeColor"
        class="py-4 px-3"
        @finish-notice-colors="onFinishNoticeColors"
      />
      <v-divider></v-divider>

      <v-card-text
        class="d-flex flex-grow-1 flex-column justify-space-between"
      >
        <div class="d-flex align-center justify-space-between">
          <div>
            <v-badge
              :content="customerCount"
              inline
            >
              <v-icon
                large
              >
                {{ icons.mdiAccountOutline }}
              </v-icon>
            </v-badge>
          </div>

          <div class="d-flex">
            <sup>
              <v-icon>
                {{ icons.mdiCurrencyJpy }}
              </v-icon>
            </sup>
            <span
              class="text-2xl font-weight-semibold"
            >
              {{ total.toLocaleString() }}
            </span>
          </div>
        </div>

        <div
          v-if="$can('manage', 'waiter:deposits')"
          class="d-flex align-center mt-1"
        >
          <v-btn
            x-small
            text
            class="px-1"
            :ripple="false"
            @click.stop="depositDialog = true"
          >
            <v-icon
              x-small
              left
            >
              {{ icons.mdiCashMultiple }}
            </v-icon>
            <span class="text-caption">
              {{ totalDepositAmount > 0 ? `お預かり ¥${totalDepositAmount.toLocaleString()}` : 'お預かり' }}
            </span>
          </v-btn>
        </div>

        <div
          v-if="customerNames"
          class="d-flex align-center customer-names-row"
        >
          <v-icon
            left
            small
          >
            {{ icons.mdiAccountCircleOutline }}
          </v-icon>
          <span class="customer-names text-caption text--secondary">
            {{ customerNames }}
          </span>
        </div>

        <div
          class="d-flex align-center"
          style="min-height: 36px"
        >
          <v-icon
            left
            small
          >
            {{ icons.mdiVectorLink }}
          </v-icon>
          <v-chip-group
            v-if="referringHistories.length > 0"
            column
          >
            <span
              v-for="referringHistory in referringHistories"
              :key="`referringHistory${referringHistory.id}`"
            >
              {{ referringHistory.attributes.user.data.attributes.name }}
              <v-chip small>
                {{ referringHistory.attributes.referral.data.attributes.name }}
              </v-chip>
            </span>
          </v-chip-group>

          <span v-else>---</span>
        </div>

        <nomination-display
          :nomination-histories="currentNominations"
        />
      </v-card-text>

      <v-menu
        top
        left
      >
        <template #activator="{ on: menuOn, attrs: menuAttrs }">
          <v-btn
            icon
            absolute
            style="bottom: 0; right: 0;"
            v-bind="menuAttrs"
            v-on="menuOn"
          >
            <v-icon
              small
              v-text="icons.mdiDotsVertical"
            />
          </v-btn>
        </template>

        <template #default="menu">
          <v-list
            dense
            outlined
          >
            <v-subheader>操作</v-subheader>
            <v-list-item class="mb-4">
              <v-list-item-content>
                <v-list-item-title class="d-flex align-center">
                  <span class="mr-4">移動</span>
                  <div @click.stop>
                    <v-select
                      v-model="moveTableParams.tableNumber"
                      :items="tablesToMove"
                      :prepend-icon="icons.mdiArrowRightBold"
                      dense
                      hide-details
                      @change="onMoveTableSelect($event, menu)"
                    />
                  </div>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-dialog
              v-model="closeDialog"
              max-width="400px"
            >
              <template #activator="{ on: listOn, attrs: listAttrs }">
                <v-list-item
                  v-bind="listAttrs"
                  v-on="listOn"
                >
                  <v-list-item-title class="error--text">
                    卓を削除
                  </v-list-item-title>
                </v-list-item>
              </template>
              <template #default="dialog">
                <v-card>
                  <v-spacer />
                  <v-toolbar
                    flat
                    color="transparent"
                  >
                    削除の確認
                  </v-toolbar>
                  <v-card-text>
                    削除を実行してもよろしいですか（この卓に紐づけたすべての履歴も削除します。）
                  </v-card-text>
                  <v-card-actions class="justify-end">
                    <v-btn
                      text
                      @click="() => {
                        dialog.value = false;
                        menu.value = false;
                      }"
                    >
                      キャンセル
                    </v-btn>
                    <v-btn
                      color="error"
                      @click="deleteTable"
                    >
                      同意の上で削除
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </template>
            </v-dialog>
          </v-list>
        </template>
      </v-menu>
    </v-card>

    <v-dialog
      v-model="depositDialog"
      max-width="480px"
    >
      <deposit-section
        :table-id="id"
        :initial-deposits="deposits"
        @updated:table="$emit('updated:table', $event)"
        @close="depositDialog = false"
      />
    </v-dialog>
  </div>
</template>

<script>
import {
  reactive,
  computed,
  inject,
  ref,
} from '@vue/composition-api'
import { map } from 'lodash'
import {
  mdiAccountOutline,
  mdiAccountCircleOutline,
  mdiCurrencyJpy,
  mdiVectorLink,
  mdiPound,
  mdiDotsVertical,
  mdiArrowRightBold,
  mdiCashMultiple,
} from '@mdi/js'
import TableName from '@/views/components/table/TableName.vue'
import TableTimer from '@/views/components/table/TableTimer.vue'
import NominationDisplay from '@/views/components/table/NominationDisplay.vue'
import DepositSection from '@/views/components/table/TableShowDrawer/Overview/DepositSection.vue'
import AutoExtensionBadge from '@/views/components/table/AutoExtensionBadge.vue'

export default {
  components: {
    TableName,
    TableTimer,
    NominationDisplay,
    DepositSection,
    AutoExtensionBadge,
  },
  props: {
    id: {
      type: [Number, String],
      required: true,
    },
    hallId: {
      type: [Number, String],
      required: true,
    },
    tablePrefix: {
      type: String,
      default: '',
    },
    tableSuffix: {
      type: String,
      default: '卓',
    },
    tableNumber: {
      type: [String, Number],
      required: true,
    },
    customName: {
      type: String,
      default: '',
    },
    startAt: {
      type: String,
      required: true,
    },
    expireAt: {
      type: String,
      required: false,
      default: null,
    },
    autoExtensionEnabled: {
      type: Boolean,
      default: false,
    },
    customerCount: {
      type: [Number, String],
      required: true,
    },
    subtotal: {
      type: [Number, String],
      required: true,
    },
    total: {
      type: [Number, String],
      required: true,
    },
    totalDepositAmount: {
      type: [Number, String],
      default: 0,
    },
    deposits: {
      type: Array,
      default: () => [],
    },
    currentNominations: {
      // NOTE: 中身はNominationだけのvending_history
      type: Array,
      default: () => [],
    },
    referringHistories: {
      type: Array,
      default: () => [],
    },
    customerTableTaggings: {
      type: Array,
      default: () => [],
    },
    customers: {
      type: Array,
      default: () => [],
    },
    finishNoticeMinutesDate: {
      type: Object,
      default: () => ({
        first: null,
        second: null,
      }),
    },
    finishNoticeColor: {
      type: String,
      default: null,
    },
    colorCodes: {
      type: Object,
      default: () => ({
        red: '#D74A4A',
        yellow: '#F4C645',
      }),
    },
  },
  setup(props, { emit }) {
    const halls = inject('halls', [])
    const closeDialog = ref(false)
    const depositDialog = ref(false)

    const moveTableParams = reactive({
      tableNumber: null,
      hallId: null,
    })

    const addedCustomerTags = computed(() => {
      if (props.customerTableTaggings.length < 1) return []

      return map(props.customerTableTaggings, 'attributes.customerTag.data')
    })

    const customerNames = computed(() => {
      if (!props.customers || props.customers.length === 0) return ''

      return props.customers.map(c => c.name).join('/')
    })

    const tableName = computed(() => {
      if (props.customName) return props.customName

      return [props.tablePrefix, props.tableNumber, props.tableSuffix].join('')
    })

    const sortedHalls = computed(() => {
      return halls.value.slice().sort((a, b) => {
        return a.attributes.position - b.attributes.position
      })
    })

    const tablesToMove = computed(() => {
      return sortedHalls.value.flatMap(hall => {
        // 現在進行中のテーブル番号を取得
        const ongoingTableNumbers = hall.attributes.tables.data.map(table => table.attributes.tableNumber)

        const layouts = hall.attributes.tableLayouts || []

        // テーブルリスト生成
        const tables = Array.from({ length: hall.attributes.tableCount }, (_, i) => {
          const tableNumber = i + 1

          // 現在進行中のテーブルを除外
          if (ongoingTableNumbers.includes(tableNumber)) return null

          const customName = layouts.find(l => Number(l.table_number) === tableNumber)?.name
          const text = customName || [hall.attributes.tablePrefix, tableNumber, hall.attributes.tableSuffix].join('')

          return {
            text,
            value: `${hall.id}-${tableNumber}`,
          }
        }).filter(Boolean) // null を除外

        return [{ header: hall.attributes.name }, ...tables]
      })
    })

    const onMoveTableSelect = (value, menu) => {
      // eslint-disable-next-line no-param-reassign
      const callback = () => (menu.value = false)

      const [hallId, tableNumber] = value.split('-')
      moveTableParams.hallId = Number(hallId)
      moveTableParams.tableNumber = Number(tableNumber)

      emit('move-table', { hallId, tableNumber, callback })
    }

    const deleteTable = () => {
      emit('destroy')
    }

    const onFinishNoticeColors = color => {
      const hallId = sortedHalls.value.find(hall => hall.id === props.hallId)?.id
      emit('finish-notice-colors', { hallId: +hallId, tableId: +props.id, color })
    }

    // 枠線色はTableTimerが判定・emitするfinishNoticeColorが唯一の決定者。
    // ここで時刻ベースの再計算を持つと毎秒×卓数の再描画が発生するため持たない
    const borderStyle = computed(() => {
      if (props.finishNoticeColor === 'red') {
        return `border: 3px solid ${props.colorCodes.red}; background-color: ${props.colorCodes.red}20;`
      }

      if (props.finishNoticeColor === 'yellow') {
        return `border: 3px solid ${props.colorCodes.yellow}; background-color: ${props.colorCodes.yellow}20;`
      }

      return ''
    })

    return {
      moveTableParams,
      halls,
      closeDialog,
      depositDialog,

      // computed
      addedCustomerTags,
      customerNames,
      tableName,
      tablesToMove,
      borderStyle,

      // methods
      onMoveTableSelect,
      deleteTable,
      onFinishNoticeColors,

      icons: {
        mdiAccountOutline,
        mdiAccountCircleOutline,
        mdiCurrencyJpy,
        mdiVectorLink,
        mdiPound,
        mdiDotsVertical,
        mdiArrowRightBold,
        mdiCashMultiple,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.customer-tags-cap-slide {
  .v-slide-group__prev--disabled, .v-slide-group__next--disabled {
    display: none;
  }
}

.customer-names-row {
  min-height: 24px;
}

.customer-names {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
}
</style>
